import { Button, stopPropagation, Text } from "@overmap-ai/blocks"
import {
	type AssetTypeIdentifier,
	type AssetTypeStatus,
	type Issue,
	type Stored,
	UNLABELLED_ASSET_LABEL,
} from "@overmap-ai/common"
import { selectIssuesByIds } from "@overmap-ai/core"
import { FieldSchema } from "@overmap-ai/forms"
import { useNavigate } from "@tanstack/react-router"
import {
	type ColumnDef,
	getCoreRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	type TableOptions,
	useReactTable,
} from "@tanstack/react-table"
import { useCallback, useMemo } from "react"

import { useShallowEqaulSelector } from "../../../store"
import type { AssetData } from "../../../typings"
import { IssueDropdownMenu } from "../../DropdownMenus/IssueDropdownMenu"
import { AssetTypeStatusIcon } from "../../Icons/AssetTypeStatusIcon"
import { IssueIcon } from "../../Icons/IssueIcon"
import { AssetIndexLabel } from "../../IndexLabels/AssetIndexLabel"
import { IssueIndexLabel } from "../../IndexLabels/IssueIndex"
import { IssueLink } from "../../Links"
import { ColumnSortButton, ColumnSortIcon, RowSelectAllCheckbox, RowSelectCheckbox } from "../components"
import { getColumnDefsOfFieldSchema, getColumnDefsOfIdentifiers } from "../utils"

export interface AssetTableColumnsConfig {
	assetTypeIdentifiers: Stored<AssetTypeIdentifier>[]
	assetTypeFieldSchema: FieldSchema
	assetTypeStatuses: Stored<AssetTypeStatus>[]
}

export const useAssetTableColumns = (config: AssetTableColumnsConfig) => {
	const { assetTypeIdentifiers, assetTypeFieldSchema, assetTypeStatuses } = config

	return useMemo<ColumnDef<AssetData>[]>(() => {
		// DEFAULT COLUMNS
		const columnDefs: ColumnDef<AssetData>[] = [
			{
				id: "select",
				enableHiding: false,
				header({ table }) {
					return (
						<RowSelectAllCheckbox
							toggleAllRowsSelected={table.toggleAllRowsSelected}
							isSomeRowsSelected={table.getIsSomeRowsSelected()}
							isAllRowsSelected={table.getIsAllRowsSelected()}
						/>
					)
				},
				cell({ row }) {
					return (
						<RowSelectCheckbox isRowSelected={row.getIsSelected()} toggleRowSelected={row.toggleSelected} />
					)
				},
			},
			{
				id: "index",
				accessorFn: (originalRow) => originalRow.asset.index,
				meta: {
					plainHeader: "Index",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Index
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: function Cell({ row }) {
					const asset = row.original.asset
					return <AssetIndexLabel highContrast assetId={asset.uuid} />
				},
			},
			{
				id: "label",
				meta: {
					plainHeader: "Label",
				},
				accessorFn: (data) => data.asset.label,
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							{column.columnDef.meta?.plainHeader}
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ row }) {
					const asset = row.original.asset
					return <Text accentColor="base">{asset.label || UNLABELLED_ASSET_LABEL}</Text>
				},
			},
			{
				id: "linked_to",
				accessorFn: (row) => row.issueAssociations.length,
				meta: {
					plainHeader: "Linked to",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							{column.columnDef.meta?.plainHeader}
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: function Cell({ row }) {
					const issueAssociations = row.original.issueAssociations

					const navigate = useNavigate()
					const issues = useShallowEqaulSelector(
						selectIssuesByIds(issueAssociations.map((association) => association.issue)),
					)

					const handleSelectIssue = useCallback(
						(issue: Stored<Issue>) => {
							void navigate({
								from: "/$organizationId/$projectId",
								to: "/$organizationId/$projectId/issues/$issueId",
								params: (prev) => ({
									...prev,
									issueId: issue.uuid,
								}),
							})
						},
						[navigate],
					)

					const getRender = () => {
						switch (issueAssociations.length) {
							case 0:
								return <Text accentColor="base">None</Text>
							case 1: {
								const issueId = issueAssociations[0].issue

								return (
									<IssueLink issueId={issueId} onClick={stopPropagation}>
										<Button size="sm" accentColor="base" variant="ghost">
											<IssueIcon issueId={issueId} background={false} />
											<IssueIndexLabel issueId={issueId} />
										</Button>
									</IssueLink>
								)
							}
							default:
								return (
									<IssueDropdownMenu issues={issues} onSelectIssue={handleSelectIssue} size="sm">
										<Button size="sm" accentColor="base" variant="ghost" onClick={stopPropagation}>
											{issueAssociations.length} linked issues
										</Button>
									</IssueDropdownMenu>
								)
						}
					}

					return getRender()
				},
			},
		]

		// ASSET IDENTIFIER COLUMNS
		if (assetTypeIdentifiers.length > 0) {
			columnDefs.push(
				...getColumnDefsOfIdentifiers<Stored<AssetTypeIdentifier>, AssetData>(
					assetTypeIdentifiers,
					(identifier) => (data) => data.assetTypeIdentifierValues[identifier.uuid]?.value,
				),
			)
		}

		// ASSET STATUS COLUMN
		if (assetTypeStatuses.length > 0) {
			columnDefs.push({
				id: "status",
				meta: {
					plainHeader: "Status",
				},
				accessorFn: (data) => data.asset.status,
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							{column.columnDef.meta?.plainHeader}
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ row }) {
					const statusId = row.original.asset.status
					if (!statusId) return <Text accentColor="base">None</Text>
					const assetTypeStatus = assetTypeStatuses.find(({ uuid }) => uuid === statusId)
					return (
						<div className="flex items-center gap-2">
							<AssetTypeStatusIcon assetTypeStatusId={statusId} />
							<Text>{assetTypeStatus?.name}</Text>
						</div>
					)
				},
			})
		}

		// ASSET TYPE FIELDS COLUMNS
		if (assetTypeFieldSchema.getFields().length > 0) {
			columnDefs.push(
				...getColumnDefsOfFieldSchema<AssetData>(assetTypeFieldSchema, (data) =>
					assetTypeFieldSchema.deserializeValues(data.assetTypeFieldValues?.values ?? {}),
				),
			)
		}

		return columnDefs
	}, [assetTypeFieldSchema, assetTypeIdentifiers, assetTypeStatuses])
}

export const useAssetTable = (args: Omit<TableOptions<AssetData>, "getCoreRowModel">) => {
	return useReactTable<AssetData>({
		...args,
		getRowId: (originalRow) => originalRow.asset.uuid,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
	})
}
